import { Star, Trash2, ExternalLink, Gift, Check } from "lucide-react";
import type { WishItem } from "@/services/wishlists";
import BlurImage from "./BlurImage";

interface Props {
  item: WishItem;
  isOwner: boolean;
  isAuthenticated: boolean;
  onDelete?: (id: string) => void;
  onClaim?: (id: string) => void;
  compact?: boolean;
}

const WishlistItemCard = ({ item, isOwner, isAuthenticated, onDelete, onClaim }: Props) => {
  const isClaimed = !!item.is_claimed;

  return (
    <div
      className={`group rounded-2xl border overflow-hidden transition-all relative bg-card ${
        isClaimed
          ? "border-border/50 opacity-60"
          : "border-border/60 hover:border-primary/30 hover:shadow-lg"
      }`}
    >
      {/* Delete button (owner) */}
      {isOwner && onDelete && (
        <button
          onClick={() => onDelete(item.id)}
          className="absolute top-2 end-2 z-10 p-1.5 sm:p-2 rounded-full bg-background/80 backdrop-blur-sm text-muted-foreground hover:text-destructive hover:bg-background transition-all opacity-0 group-hover:opacity-100"
        >
          <Trash2 size={12} className="sm:hidden" />
          <Trash2 size={14} className="hidden sm:block" />
        </button>
      )}

      {/* Most desired badge */}
      {item.is_most_desired && !isClaimed && (
        <div className="absolute top-2 start-2 z-10 bg-primary text-primary-foreground rounded-full w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center shadow-sm">
          <Star size={10} fill="currentColor" className="sm:hidden" />
          <Star size={12} fill="currentColor" className="hidden sm:block" />
        </div>
      )}

      {/* Image */}
      <div className="relative w-full aspect-square bg-muted/30 overflow-hidden flex items-center justify-center p-2 sm:p-3">
        {item.image_url ? (
          <BlurImage
            src={item.image_url}
            alt={item.title}
            className={`max-w-full max-h-full object-contain transition-transform duration-300 group-hover:scale-105 ${isClaimed ? "grayscale" : ""}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-muted/40 to-muted/80 rounded-lg">
            <Gift size={22} className="text-muted-foreground/20 sm:hidden" />
            <Gift size={28} className="text-muted-foreground/20 hidden sm:block" />
          </div>
        )}

        {/* Claimed overlay */}
        {isClaimed && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/30 backdrop-blur-[1px]">
            <div className="bg-foreground text-background rounded-full px-2.5 py-0.5 sm:px-3 sm:py-1 flex items-center gap-1">
              <Check size={9} strokeWidth={3} className="sm:hidden" />
              <Check size={11} strokeWidth={3} className="hidden sm:block" />
              <span className="text-[9px] sm:text-[10px] font-bold uppercase tracking-wider">Claimed</span>
            </div>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="px-2 sm:px-3 pt-1.5 sm:pt-2 pb-2 sm:pb-3 space-y-1 sm:space-y-1.5">
        <p className="text-xs sm:text-sm font-semibold text-foreground line-clamp-2 leading-snug">
          {item.title}
        </p>

        {item.price != null && (
          <p className="text-xs sm:text-sm font-bold text-foreground">{item.price} AED</p>
        )}

        <div className="flex items-center justify-between">
          {isClaimed && item.claimed_by_name && (
            <p className="text-[9px] sm:text-[10px] text-muted-foreground truncate">
              By {item.claimed_by_name}
            </p>
          )}
          {item.link_url && (
            <a
              href={item.link_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-[9px] sm:text-[10px] font-medium text-primary/80 hover:text-primary transition-colors ms-auto"
            >
              <ExternalLink size={8} className="sm:hidden" />
              <ExternalLink size={10} className="hidden sm:block" />
              View
            </a>
          )}
        </div>

        {/* Claim button */}
        {!isClaimed && !isOwner && isAuthenticated && onClaim && (
          <button
            onClick={() => onClaim(item.id)}
            className="w-full text-[10px] sm:text-xs font-bold text-secondary-foreground bg-secondary hover:bg-secondary/80 rounded-full py-1.5 sm:py-2 transition-all mt-0.5"
          >
            I'll get this
          </button>
        )}
      </div>
    </div>
  );
};

export default WishlistItemCard;
